import http from '@/plugins/api'
import * as types from './types'
import { startLoading } from '@/helpers/loading'
import storage from '@/plugins/storage'
import {
  appId,
  apiUrl,
  authUrl,
  lookupUrl
} from '@/plugins/globals.js'

const unknownError = 999

function normalizeUserProfile(apiData) {
  if (!apiData) return {}
  console.log('DEBUG => [ACTIONS] => Normalizing user profile:', apiData);
  return {
    userId: apiData.UserId,
    firstName: apiData.FirstName,
    lastName: apiData.LastName,
    phone: apiData.Phone || '',
    userName: apiData.Username,
    email: apiData.Email,
    role: apiData.role || '',
    roles: apiData.Roles || [],
    businessName: apiData.BusinessName || '',
    location: {
      id: apiData?.Location?.Id || null,
      name: apiData?.Location?.Name || null,
      key: ''
    },
    isUK: apiData.IsUK || false,
    isDeleted: apiData.IsDeleted || false,
    isMahle: apiData.IsMahle || false,
    isLockedOut: apiData.IsLockedOut || false,
    isActive: apiData.IsActive || false,
    asTechURL: apiData.asTechURL || '',
    language: apiData.Language || 'en',
  }
}

function collectChildValidation($this, validations) {
  $this.$children.forEach(vm => {
    validations.push(vm.$validator.validateAll())
    if (vm.$children.length) {
      collectChildValidation(vm, validations)
    }
  })
}

function getMahleApiErrorMsg(errorNo, msg) {
  const genericMsg = 'Check the status of the Wifi connection, try again and if the problem persists, please contact technical assistance.'
  const messages = {
    100: 'Generic unmanaged error.',
    200: `Mandatory data missing: (${msg}).`,
    204: 'No coin data available.',
    300: 'Wrong username.',
    400: 'SN is not associated with any of our devices.',
    500: 'Missing registration data.',
    600: 'Diagnostic Licence Expired.',
    700: 'Some mandatory fields are missing in the submitted json report.',
    800: 'The supplied json report is not valid json format.',
    900: 'The work order number is required but hasn\'t been supplied.'
  }
  return `${messages[errorNo] || 'Unknown error.'} \r\n ${genericMsg}`
}

export default {
  clearToken({ commit }) {
    commit(types.RESET_LOGIN)
  },

  setPassthrough({ commit }, passthrough) {
    commit(types.SET_PASSTHROUGH, passthrough)
  },

  setSolution({ commit }, solution) {
    commit(types.SET_SOLUTION, solution)
  },

  async getToken({ commit, getters, dispatch }, [user, pwd, demoMode, solution]) {
    try {
      // Reset login UI state
      commit(types.RESET_LOGIN);

      // Persist solution + demoMode choices
      commit(types.SET_SOLUTION, solution);
      await storage.local.setItem(types.SESSION_SOLUTION, solution);

      const baseUrl = await authUrl();
      const token = await startLoading(dispatch, 'getToken', () =>
        http.post('getToken', { user, pwd }, {}, baseUrl)
      );

      // Save raw token
      commit(types.SET_TOKEN, token.data);
      await storage.local.setItem(types.SESSION_TOKEN_NAME, token.data);

      commit(types.SET_DEMO_MODE, demoMode);
      await storage.local.setItem(types.DEMO_MODE, demoMode);

      // If backend returned a "user" with a principal
      if (getters.user?.prn) {
        // 1) Fetch user details
        const userInfo = await startLoading(dispatch, 'getUserByName', () =>
          http.get('user/getUserByName', { params: { username: user } })
        );
        const userData = userInfo.data[0];

        // 2) Roles -> names only (persist too)
        const roleNames = (userData.Roles || []).map(r => r.Name).filter(Boolean);
        commit(types.SET_ROLES, roleNames);
        await storage.local.setItem(types.SESSION_ROLES_NAME, JSON.stringify(roleNames));

        // 3) RAG flags
        const hasRagOnlyRole = !!((userData.Roles || []).some(role => role.Id === 14));
        const hasShowRagPermission = userData.ShowRAG === true || userData.ShowRAG === 1;
        commit('SET_USER_RAG_ONLY', hasRagOnlyRole);
        await storage.local.setItem(types.SESSION_RAG_ONLY, hasRagOnlyRole);
        commit('SET_USER_SHOW_RAG', hasShowRagPermission);
        await storage.local.setItem(types.SESSION_SHOW_RAG, hasShowRagPermission);

        // 4) Normalize + persist profile
        const normalizedProfile = normalizeUserProfile(userData);
        commit(types.SET_USER_ISMAHLE, normalizedProfile.isMahle);
        await storage.local.setItem(types.SESSION_USER_ISMAHLE, normalizedProfile.isMahle);

        commit(types.SET_USER_PROFILE, normalizedProfile);
        await storage.local.setItem(types.SESSION_USER_PROFILE, JSON.stringify(normalizedProfile));

        // 5) Primary role (existing behavior)
        const primaryRole = getters.primaryRole;
        commit(types.SET_PRIMARY_ROLE, primaryRole);
        await storage.local.setItem(types.SESSION_PRIMARY_ROLE, JSON.stringify(primaryRole));

        // 6) Also store “user token” the way you already did
        commit(types.SET_USER_TOKEN, token.data);
        await storage.local.setItem(types.SESSION_USER_TOKEN, JSON.stringify(token.data));

        // 7) Contact + location info
        const contactId = await startLoading(dispatch, 'auth/getContactIdByUserId', () =>
          http.get(`user/getContactIdByUserId?userId=${normalizedProfile.userId}`)
        );
        console.log('DEBUG => [ACTIONS=>getToken] => SET_CONTACT_ID: ', contactId.data, ', userId: ', normalizedProfile)
        commit(types.SET_CONTACT_ID, contactId.data);

        const locationKey = await startLoading(dispatch, 'auth/getLocationKey', () =>
          http.get(`BusinessQuery/LocationById?id=${normalizedProfile?.location?.id}`)
        );
        commit(types.SET_LOCATION_KEY, locationKey.data);

        // Keep USER_PROFILE in storage in sync with any reactive mutations
        const updatedUserProfile = getters.userProfile;
        console.log('DEBUG => [ACTIONS=>getToken] => SESSION_USER_PROFILE:  userId: ', updatedUserProfile)
        await storage.local.setItem(types.SESSION_USER_PROFILE, JSON.stringify(updatedUserProfile));

        // 8) Language
        commit(types.SET_LANGUAGE, normalizedProfile.language || 'en');
        await storage.local.setItem(types.SESSION_LANGUAGE, normalizedProfile.language || 'en');

        // 9) Mock Mahle API status (unchanged)
        commit(types.SET_MAHLE_COIN_COUNT, 200);
        await storage.local.setItem(types.SESSION_MAHLE_COIN_BALANCE, 200);

        commit(types.SET_MAHLE_DIAGNOSTIC_LICENSE_STATUS, 'Active');
        await storage.local.setItem(types.SESSION_MAHLE_DIAGNOSTIC_LICENSE_STATUS, 'Active');

        commit(types.SET_MAHLE_API_ERROR_MSG, '');
        await storage.local.setItem(types.SESSION_MAHLE_API_ERROR_MSG, '');

        // a) Restore any previously chosen chat identity for this browser
        await dispatch('hydrateChatAs'); // reads SESSION_CHAT_AS -> state.chatAs

        // b) Safety: if SESSION_CHAT_AS belongs to a *different* logged-in user, clear it
        const ownerKey = normalizedProfile.email; // the logged-in user's unique key
        const savedOwner = await storage.local.getItem(types.SESSION_CHAT_AS_OWNER);
        if (savedOwner && savedOwner !== ownerKey) {
          await dispatch('clearChatAs'); // wipes state + SESSION_CHAT_AS + SESSION_CHAT_AS_OWNER
        }

        // c) Mahle users always chat as themselves (no profile screen)
        if (normalizedProfile.isMahle) {
          await dispatch('setChatAs', {
            email: normalizedProfile.email,
            firstName: normalizedProfile.firstName,
            lastName: normalizedProfile.lastName,
            phone: normalizedProfile.phone
          });
        }

        // Return for router logic
        return { userData, hasRagOnlyRole };
      }
    } catch (err) {
      console.error('Error in getToken:', err);
    }

    return { userData: null, hasRagOnlyRole: false };
  },

  async clearChatAs({ commit }) {
    commit(types.SET_CHAT_AS, null);
    await Promise.all([
      storage.local.setItem(types.SESSION_CHAT_AS, ''),
      storage.local.setItem(types.SESSION_CHAT_AS_OWNER, '')
    ]);
  },

  // Restore chatAs from storage (if any)
  async hydrateChatAs({ commit }) {
    try {
      const savedStr = window.localStorage.getItem(types.SESSION_CHAT_AS) || '';
      const savedOwner = window.localStorage.getItem(types.SESSION_CHAT_AS_OWNER) || '';

      const profileStr = window.localStorage.getItem(types.SESSION_USER_PROFILE) || '';
      let currentOwner = '';
      try { currentOwner = (JSON.parse(profileStr) || {}).email || ''; } catch {}

      if (savedStr && savedOwner && savedOwner === currentOwner) {
        let parsed = null;
        try { parsed = JSON.parse(savedStr); } catch {}
        if (parsed && parsed.email) {
          commit(types.SET_CHAT_AS, parsed);
          return;
        }
      }
    } catch {}
    commit(types.SET_CHAT_AS, null);
  },

  // Set chatAs to a specific contact and persist it (also stamp owner)
  async setChatAs({ commit }, contact) {
    const chatAs = {
      email: contact?.email || '',
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
    };
    commit(types.SET_CHAT_AS, chatAs);
    await storage.local.setItem(types.SESSION_CHAT_AS, JSON.stringify(chatAs));

    // owner must be the true logged-in user, not the contact
    let ownerEmail = '';
    try {
      const raw = await storage.local.getItem(types.SESSION_USER_PROFILE);
      const persisted = raw ? JSON.parse(raw) : null;
      ownerEmail = persisted?.email || '';
    } catch {}

    await storage.local.setItem(types.SESSION_CHAT_AS_OWNER, ownerEmail || '');
  },

  async veeValidateAll(_, $this) {
    try {
      const validations = [$this.$validator.validateAll()]
      collectChildValidation($this, validations)
      const results = await Promise.all(validations)
      return results.every(i => i === true)
    } catch (err) {
      console.error('Validation error:', err)
      return false
    }
  },

  logout({ commit }) {
    commit(types.RESET_LOGIN)
  },

  setContact({ commit }, id) {
    commit(types.SET_CONTACT, id)
  },

  setUserInfo({ commit }, userInfo) {
    commit(types.SET_USERINFO, userInfo)
  },

  setPhone({ commit }, phone) {
    commit(types.SET_PHONE, phone)
  },

  updateContact({ commit }, data) {
    commit(types.UPDATE_CONTACT, data)
  },

  async setLanguage({ commit, dispatch }, contact) {
    commit(types.SET_LANGUAGE, contact.language)
    const appIdRaw = appId()
    console.log('DEBUG => [ACTIONS => setLanguage] => appId: ', appIdRaw, ', contact: ', contact)
    if (appIdRaw !== 2) {
      await startLoading(dispatch, 'setLanguage', () =>
        http.post('User/UpdateContact', contact)
      )
    }
  },

  showLogging({ commit }, isShow) {
    commit(types.SHOW_LOGGING, isShow)
  },

  setLogging({ commit }, data) {
    commit(types.SET_LOGGING, data)
  },

  setLogEntry({ commit }, data) {
    commit(types.SET_LOG_ENTRY, data)
  },

  clearLog({ commit }) {
    commit(types.CLEAR_LOG)
  },

  async setNotificationToken({ commit, dispatch }, token) {
    commit(types.SET_NOTIFICATION_TOKEN, token.token)
    await startLoading(dispatch, 'setNotificationToken', () =>
      http.post('User/SetFirebaseToken', token)
    )
  },

  async getLocationKey({ commit, dispatch }, id) {
    const result = await startLoading(dispatch, 'auth/getLocationKey', () =>
      http.get(`BusinessQuery/LocationById?id=${id}`)
    )
    commit(types.SET_LOCATION_KEY, result.data)
  },

  async getBusinessUsers({ commit, dispatch, state }) {
    if (!state.userProfile) {
      console.warn('[auth/getBusinessUsers] userProfile is null, trying to re-initialize state...');
      await dispatch('initState');
    }

    if (!state.userProfile?.location?.key) {
      console.error('[auth/getBusinessUsers] Missing location key for userProfile');
      return;
    }

    const result = await startLoading(dispatch, 'auth/getBusinessUsers', () =>
      http.get(`BusinessQuery/Users?key=${state.userProfile?.location?.key}`)
    );
    commit(types.SET_BUSINESS_USERS, result.data);
  },

  async upsertBusinessContact(_, contact) {
    const result = await http.post('Business/ModifyUsers', contact)
    return result.data
  },

  async getBusinessContact(_, contactId) {
    if (!contactId) {
      console.warn('[auth/getBusinessContact] No contactId provided – skipping API call');
      return null;
    }
    const result = await http.get(`BusinessQuery/BusinessContactGet?id=${contactId}`)
    return result.data
  },

  async checkIfBanned({ commit, state, dispatch }, solution) {
    if (!state.userToken) return null

    const url = lookupUrl(solution)
    const result = await startLoading(dispatch, 'auth/checkIfBanned', () =>
      http.get(`User/CheckIfBanned?id=${state.userToken.id}&token=${state.userToken.token}`, {}, {}, url)
    )
    commit(types.SET_USER_INFO, result.data)
    return result.data
  },

  async setMahleDisplayValues({ commit, dispatch }, [asTechSn]) {
    if (!asTechSn) {
      console.warn('[auth/setMahleDisplayValues] Missing asTechSn');
      return;
    }

    commit(types.RESET_MAHLE_COIN_BALANCE);
    commit(types.RESET_MAHLE_DIAGNOSTIC_LICENSE_STATUS);
    commit(types.RESET_MAHLE_API_ERROR_MSG);

    try {
      const result = await startLoading(dispatch, 'auth/getMahleCoinBalance', () =>
        http.get(`Mahle/CurrentCoinBalance?asTechSn=${asTechSn}`)
      );

      const { data } = result;

      if (data?.status === false) {
        commit(types.SET_MAHLE_COIN_COUNT, '?');
        commit(types.SET_MAHLE_DIAGNOSTIC_LICENSE_STATUS, data.error === 600 ? 'Expired' : 'Active');
        commit(types.SET_MAHLE_API_ERROR_MSG, getMahleApiErrorMsg(data.error || unknownError, data.errorText || ''));
      } else {
        commit(types.SET_MAHLE_COIN_COUNT, data.error === 0 ? data.balance : '?');
        commit(types.SET_MAHLE_DIAGNOSTIC_LICENSE_STATUS, data.error === 600 ? 'Expired' : 'Active');
      }
    } catch (e) {
      console.error('Mahle API call failed:', e);
      commit(types.SET_MAHLE_COIN_COUNT, '?');
      commit(types.SET_MAHLE_DIAGNOSTIC_LICENSE_STATUS, '?');
      commit(types.SET_MAHLE_API_ERROR_MSG, getMahleApiErrorMsg(unknownError, ''));
    }
  },

  async insertAuditInfo(_, auditInfo) {
    const result = await http.post('TC/AddAuditInfo', auditInfo)
    return result.data
  },

  async getTermAndConditionsUIMessage({ commit }) {
    const result = await http.get('TC/GetUIMessage')
    commit(types.SET_TERMANDCONDITIONS_UI_MESSAGE, result.data)
  },

  async downloadCurrentTermsAndConditionDocument() {
    const url = `${apiUrl()}/TC/TermsAndConditions`
    window.open(url, '_blank')
  },

  async initState({ commit, dispatch, state }) {
    let success = false;
    console.log('DEBUG => [initState] => [auth] Initializing auth state...');
    const token = await storage.local.getItem(types.SESSION_TOKEN_NAME);

    // Check for missing or invalid token
    if (!token || token === 'null' || token === 'undefined') {
      console.warn('DEBUG => [initState] => [auth] No valid token found in storage, clearing session.');
      commit(types.RESET_LOGIN);
      await storage.local.clear();
    }

    try {
      const [
        rolesStr,
        userProfileStr,
        primaryRoleStr,
        userTokenStr,
        demoMode,
        language,
        solution,
        notificationToken,
        mahleDiagnosticLicenseStatus,
        mahleCoinBalance,
        mahleApiErrorMsg,
        userIsMahle,
        isRagOnly,
        showRag,
        chatAsStr,
        chatAsOwner
        // configuration
      ] = await Promise.all([
        storage.local.getItem(types.SESSION_ROLES_NAME),
        storage.local.getItem(types.SESSION_USER_PROFILE),
        storage.local.getItem(types.SESSION_PRIMARY_ROLE),
        storage.local.getItem(types.SESSION_USER_TOKEN),
        storage.local.getItem(types.DEMO_MODE),
        storage.local.getItem(types.SESSION_LANGUAGE),
        storage.local.getItem(types.SESSION_SOLUTION),
        storage.local.getItem(types.SESSION_NOTIFICATION_TOKEN),
        storage.local.getItem(types.SESSION_MAHLE_DIAGNOSTIC_LICENSE_STATUS),
        storage.local.getItem(types.SESSION_MAHLE_COIN_BALANCE),
        storage.local.getItem(types.SESSION_MAHLE_API_ERROR_MSG),
        storage.local.getItem(types.SESSION_USER_ISMAHLE),
        storage.local.getItem(types.SESSION_RAG_ONLY),
        storage.local.getItem(types.SESSION_SHOW_RAG),
        storage.local.getItem(types.SESSION_CHAT_AS),
        storage.local.getItem(types.SESSION_CHAT_AS_OWNER),
      ]);

      // Hydrate roles back as array of strings
      let rolesArr = []
      try { rolesArr = JSON.parse(rolesStr || '[]') } catch { rolesArr = [] }

      commit(types.SET_TOKEN, token);
      commit(types.SET_ROLES, rolesArr);

      let parsedProfile = null;
      let parsedPrimary = null;
      let parsedUserToken = null;
      try { parsedProfile = JSON.parse(userProfileStr || 'null'); } catch {}
      try { parsedPrimary = JSON.parse(primaryRoleStr || 'null'); } catch {}
      try { parsedUserToken = JSON.parse(userTokenStr || 'null'); } catch {}

      commit(types.SET_USER_PROFILE, parsedProfile);
      commit(types.SET_PRIMARY_ROLE, parsedPrimary);
      commit(types.SET_USER_TOKEN, parsedUserToken);

      commit(types.SET_DEMO_MODE, demoMode === 'true');
      commit(types.SET_LANGUAGE, language || '');
      commit(types.SET_SOLUTION, solution || 1);
      commit(types.SET_NOTIFICATION_TOKEN, notificationToken);
      commit(types.SET_MAHLE_DIAGNOSTIC_LICENSE_STATUS, mahleDiagnosticLicenseStatus);
      commit(types.SET_MAHLE_COIN_COUNT, mahleCoinBalance);
      commit(types.SET_MAHLE_API_ERROR_MSG, mahleApiErrorMsg);
      commit(types.SET_USER_ISMAHLE, userIsMahle);
      commit(types.SET_USER_RAG_ONLY, isRagOnly);
      commit(types.SET_USER_SHOW_RAG, showRag);

      try {
        const currentOwnerEmail = parsedProfile?.email || '';
        let savedChatAs = null;
        try { savedChatAs = JSON.parse(chatAsStr || 'null'); } catch {}

        if (chatAsOwner && chatAsOwner === currentOwnerEmail && savedChatAs && savedChatAs.email) {
          commit(types.SET_CHAT_AS, savedChatAs);
          console.log('DEBUG => [auth/initState] chatAs restored for owner', currentOwnerEmail, '=>', savedChatAs.email);
        } else {
          // Different owner or empty — clear state + storage safely (no removeItem in your storage)
          commit(types.SET_CHAT_AS, null);
          await storage.local.setItem(types.SESSION_CHAT_AS, '');
          await storage.local.setItem(types.SESSION_CHAT_AS_OWNER, '');
          console.log('DEBUG => [auth/initState] chatAs not applied (owner mismatch or empty).');
        }
      } catch (e) {
        // Safety: never break init flow because of chatAs
        commit(types.SET_CHAT_AS, null);
        await storage.local.setItem(types.SESSION_CHAT_AS, '');
        await storage.local.setItem(types.SESSION_CHAT_AS_OWNER, '');
        console.warn('DEBUG => [auth/initState] chatAs hydration failed — cleared.', e);
      }

      success = true;
    } catch (err) {
      console.error('DEBUG => [initState] => [auth] Failed to init state:', err);
      commit(types.RESET_LOGIN);
      await storage.local.clear();
    }

    // Always mark auth as initialized
    commit(types.SET_AUTH_INITIALIZED, true);
    return success;
  }
}
